import { Injectable } from '@angular/core';
import {
  AlertController,
  AlertInput,
  AlertOptions,
  AnimationController,
  LoadingController,
  LoadingOptions,
  ModalController,
  ModalOptions,
  PopoverController,
  PopoverOptions,
  ToastController,
  ToastOptions,
} from '@ionic/angular/standalone';

@Injectable({
  providedIn: 'root',
})
export class ControllerService {
  constructor(
    private loadingController: LoadingController,
    private toastController: ToastController,
    private alertController: AlertController,
    private modalController: ModalController,
    private popoverController: PopoverController,
    private animationCtrl: AnimationController
  ) {}

  loading(message?: string): Promise<HTMLIonLoadingElement> {
    const loading = this.loadingController.create({
      message: message,
    } as LoadingOptions);
    return loading;
  }

  toast(
    message: string,
    color: 'warning' | 'danger' | 'success' | 'primary' | 'light' | 'dark',
    header?: string,
  ): Promise<HTMLIonToastElement> {
    const toast = this.toastController.create({
      header: header,
      message: message,
      color: color,
      duration: 5000,
      layout: 'stacked',
      position: 'top',
      buttons: [
        {
          text: 'Dismiss',
          role: 'cancel',
        },
      ],
    } as ToastOptions);
    return toast;
  }

  toastError(message: string): Promise<HTMLIonToastElement> {
    return this.toast(message, 'danger', 'Error');
  }

  confirm(
    header: string,
    message: string,
    subHeader?: string
  ): Promise<HTMLIonAlertElement> {
    const alert = this.alertController.create({
      header: header,
      subHeader: subHeader,
      message: message,
      buttons: [
        {
          text: 'No',
          role: 'cancel',
          handler: () => {
            return false;
          },
        },
        {
          text: 'Yes',
          role: 'destructive',
        },
      ],
    } as AlertOptions);
    return alert;
  }

  select(
    header: string,
    inputs: AlertInput[],
    subHeader?: string,
    message?: string
  ): Promise<HTMLIonAlertElement> {
    const alert = this.alertController.create({
      header: header,
      subHeader: subHeader,
      message: message,
      buttons: [
        {
          text: 'Cancel',
          role: 'cancel',
          handler: () => {
            return false;
          },
        },
        {
          text: 'Select',
        },
      ],
      inputs: inputs,
    } as AlertOptions);
    return alert;
  }

  modal(component: any, componentProps: any): Promise<HTMLIonModalElement> {
    const modal = this.modalController.create({
      component: component,
      componentProps: componentProps,
      cssClass: 'my-modal-class',
      enterAnimation: this.enterAnimation,
      leaveAnimation: this.leaveAnimation,
    } as ModalOptions);
    return modal;
  }

  async dismissModal(data?: any, role?: string): Promise<boolean> {
    const top = await this.modalController.getTop();
    if (top) return this.modalController.dismiss(data, role);
    else
      return new Promise((resolve, reject) =>
        setTimeout(() => resolve(false), 0)
      );
  }

  createPopover(
    component: any,
    componentProps: any,
    event: Event
  ): Promise<HTMLIonPopoverElement> {
    const popover = this.popoverController.create({
      component: component,
      componentProps: componentProps,
      event: event,
    } as PopoverOptions);
    return popover;
  }

  enterAnimation = (baseEl: HTMLElement) => {
    const root = baseEl.shadowRoot;
    const backdropAnimation = this.animationCtrl
      .create()
      .addElement(root?.querySelector('ion-backdrop')!)
      .fromTo('opacity', '0.01', 'var(--backdrop-opacity)');
    const wrapperAnimation = this.animationCtrl
      .create()
      .addElement(root?.querySelector('.modal-wrapper')!)
      .keyframes([
        { offset: 0, opacity: '0', transform: 'scale(0)' },
        { offset: 1, opacity: '0.99', transform: 'scale(1)' },
      ]);

    return this.animationCtrl
      .create()
      .addElement(baseEl)
      .easing('ease-out')
      .duration(250)
      .addAnimation([backdropAnimation, wrapperAnimation]);
  };

  leaveAnimation = (baseEl: HTMLElement) => {
    return this.enterAnimation(baseEl).direction('reverse');
  };
}
